import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, InjectionToken, PLATFORM_ID, ValueProvider } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AudienceManagerService } from '@rci/audience-manager-service';
import * as fromCommon from '@rci/state-common';
import { Language, Province, setLanguage, setProvince, setUserLoginState } from '@rci/state-common-actions';
import { IUniversalDataLayer } from '@rogers/analytics';
import { WindowService } from '@rogers/cdk/common/services';
import { Languages, PROVINCE_DEFAULT } from '../service.constant';
import { isNil } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { skipWhile } from 'rxjs/operators';
import { RoutingService } from '@rci/routing-service';
import { CookieService } from 'ngx-cookie-service';

const COOKIE_LANGUAGE = 'language';
const COOKIE_PROVINCE = 'province';
const COOKIE_REGION = 'region';
const URL_PARAMETER_LANGUAGE = 'language';
const URL_PARAMETER_PROVINCE = 'province';

export interface StateServiceConfig {
  domain: string;
  cookieDomain: string;
}

export const STATE_SERVICE_CONFIG = new InjectionToken<StateServiceConfig>('StateServiceConfig');

export abstract class LoginStateService {
  abstract getLoginState(): LoginState;
}

@Injectable({
  providedIn: 'root',
})
export class StateService {
  language$: Observable<Language>;
  province$: Observable<Province>;
  languageAndProvince$: Observable<[Language, Province]>;
  segment$ = new BehaviorSubject<string>(undefined);
  loginState$: Observable<LoginState>;
  private _url$ = new BehaviorSubject<string | IUniversalDataLayer>(null);
  url$: Observable<string | IUniversalDataLayer> = this._url$.asObservable();

  constructor(
    @Inject(PLATFORM_ID) private readonly platform: Object,
    private readonly audienceManagerService: AudienceManagerService,
    private readonly cookieService: CookieService,
    private readonly loginStateService: LoginStateService,
    private readonly routingService: RoutingService,
    private readonly store: Store<fromCommon.State>,
    private readonly window: WindowService,
  ) {
    this.init();
  }

  private _hasGlobalHeaderFooter = true;

  get hasGlobalHeaderFooter() {
    return this._hasGlobalHeaderFooter;
  }

  static configure(config?: StateServiceConfig): ValueProvider {
    return {
      provide: STATE_SERVICE_CONFIG,
      useValue: config || {},
    };
  }

  bootstrap() {
    this.bootstrapLoginState();
    this.bootstrapSegment();
    return this.bootstrapGlobalElements$();
  }

  updateLoginState(state = {}) {
    const loginState = { ...this.loginStateService.getLoginState(), ...state };
    this.store.dispatch(setUserLoginState({ loginState }));
  }

  setLanguage(lang) {
    const language = lang.toLowerCase();
    this.store.dispatch(setLanguage({ language }));
  }

  setProvince(province) {
    const prov = province.toUpperCase();
    this.store.dispatch(setProvince({ province: prov }));
  }

  setSegment(segment) {
    this.segment$.next(segment);
  }

  setUrl(url: string | IUniversalDataLayer) {
    this._url$.next(url);
  }

  private init() {
    this.language$ = this.store.pipe(select(fromCommon.getLanguage), skipWhile(isNil));

    this.province$ = this.store.pipe(select(fromCommon.getProvince), skipWhile(isNil));

    this.languageAndProvince$ = this.store.pipe(select(fromCommon.getLanguageAndProvince));

    this.loginState$ = this.store.pipe(select(fromCommon.getUserLoginState), skipWhile(isNil));
  }

  private bootstrapGlobalElements$(): Promise<void> {
    const window = this.window.windowRef();
    return new Promise(resolve => {
      const bootstrapGlobalElements = window ? window['bootstrapGlobalElements'] : undefined;
      if (bootstrapGlobalElements && typeof bootstrapGlobalElements === 'function') {
        bootstrapGlobalElements(
          resolve,
          province => this.setProvince(province),
          language => this.setLanguage(language),
        );
      } else {
        this._hasGlobalHeaderFooter = false;
        setTimeout(() => {
          this.bootstrapLanguage();
          this.bootstrapProvince();
        });
        resolve();
      }
    });
  }

  private bootstrapLanguage() {
    this.setLanguage(
      this.routingService.getUrlQueryParameter(URL_PARAMETER_LANGUAGE) ||
        this.cookieService.get(COOKIE_LANGUAGE) ||
        (isPlatformBrowser(this.platform) && navigator.language.includes(Languages.FR) ? Languages.FR : Languages.EN),
    );
  }

  private bootstrapProvince() {
    this.setProvince(
      this.routingService.getUrlQueryParameter(URL_PARAMETER_PROVINCE) ||
        this.cookieService.get(COOKIE_PROVINCE) ||
        this.cookieService.get(COOKIE_REGION) ||
        PROVINCE_DEFAULT,
    );
  }

  private bootstrapSegment() {
    this.audienceManagerService.getSegmentByCookie$().subscribe(segment => this.setSegment(segment));
  }

  private bootstrapLoginState() {
    if (isPlatformBrowser(this.platform)) {
      // EasyLogin app triggers this function
      (<any>window).setEasyLoginHeader = () => this.updateLoginState();
    }
    this.updateLoginState();
  }
}

export interface LoginState {
  isUserAuthenticated: boolean;
  hasLastVerifiedUser: boolean;
  userName: string;
}
